<template>
    <PrimeDrawer
        v-model:visible="showDrawer"
        position="right"
        class="feedPanel"
    >
    <div class="feedPanel-header" ref="feedPanelHeader">
        <span class="headline-m">Item Feed</span>
        <CloseIcon alt="close" @click="closeDrawer" class="close" />
    </div>
    <div class="feedPanel-menu">
        <div class="feedPanel-menu-items">
            <input @input="setItemFeedTab" :checked="activeTab == 'newly-added'" v-model.lazy="activeTab" type="radio" class="feedPanel-menu-items-item" name="feedPanel-tab" id="newly-added" value="newly-added">
            <label for="newly-added" class="tab-label"><p class="paragraph-details">Newly Added</p></label>
            
            <input @input="setItemFeedTab" :checked="activeTab == 'product-updates'" v-model.lazy="activeTab" type="radio" class="feedPanel-menu-items-item" name="feedPanel-tab" id="product-updates" value="product-updates" aria-label="Show product updates">
            <label for="product-updates" class="tab-label"><p class="paragraph-details">Product Updates</p></label>

            <input @input="setItemFeedTab" :checked="activeTab == 'image-updates'" v-model.lazy="activeTab" type="radio" class="feedPanel-menu-items-item" name="feedPanel-tab" id="image-updates" value="image-updates" aria-label="Show image updates">
            <label for="image-updates" class="tab-label"><p class="paragraph-details">Image Updates</p></label>

            <input @input="setItemFeedTab" :checked="activeTab == 'stock-updates'" v-model.lazy="activeTab" type="radio" class="feedPanel-menu-items-item" name="feedPanel-tab" id="stock-updates" value="stock-updates" aria-label="Show stock updates">
            <label for="stock-updates" class="tab-label"><p class="paragraph-details">Stock Updates</p></label>
        </div>

        <div class="item-feed-container">
            <div class="complex-form">
                <div class="upload-form">
                    <span class="title-m">{{activeTabData.label}}</span>
                    <a @click.prevent="downloadReport" href="#" class="download-csv" aria-label="Download CSV file">
                        Download CSV
                    </a>
                    <!-- <label for="file-upload">Download CSV</label>
                    <input type="file" aria-label="Download CSV file" id="file-upload" title="&nbsp;"> -->
                </div>
                <div class="form-container">
                    <form>
                        <date-picker name="item_feed_date_range" style="width: 100%" range v-model="dateRange" label="Select Date Range" position="right" :max-date="new Date()"/>
                    </form>
                    <form v-if="activeTab == 'stock-updates'" class="flex-row space-between m-2">
                        <div class="input-wrapper">
                            <label for="in_stock">
                                <input type="radio" value="1" id="in_stock" name="stock_status" v-model="stockFilterValue"/>
                                Back In Stock
                            </label>
                        </div>
                        <div class="input-wrapper">
                            <label for="out_of_stock">
                                <input type="radio" value="0" id="out_of_stock" name="stock_status" v-model="stockFilterValue"/>
                                Out of Stock
                            </label>
                        </div>
                    </form>
                </div>
                <RothcoCta @click="applyFilters(currentFilters)" class="secondary small" aria-label="Apply filter">Apply filter</RothcoCta>
            </div>
            
            <div class="product-list" v-if="isLoading == false && activeTab == 'newly-added'">
                <new-list-item @navigate="closeDrawer" v-for="(rowData, i) in itemFeedResponse?.data" :key=" `${rowData.uuid}`" v-bind="{ list_item: rowData }" />
            </div>
            <div class="product-list" v-else-if="isLoading == false && activeTab == 'product-updates'">
                <product-update-list-item @navigate="closeDrawer" v-for="(rowData, i) in itemFeedResponse?.data" :key=" `${rowData.uuid}`" v-bind="{ list_item: rowData }" />
            </div>
            <div class="product-list" v-else-if="isLoading == false && activeTab == 'image-updates'">
                <image-update-list-item @navigate="closeDrawer" v-for="(rowData, i) in itemFeedResponse?.data" :key=" `${rowData.uuid}`" v-bind="{ list_item: rowData }" />
            </div>
            <div class="product-list" v-else-if="isLoading == false && activeTab == 'stock-updates'">
                <stock-update-list-item @navigate="closeDrawer" v-for="(rowData, i) in itemFeedResponse?.data" :key=" `${rowData.uuid}`" v-bind="{ list_item: rowData }" />
            </div>
            <div class="product-list" v-else>
                <div v-for="i in 20" class="product-list-item" :key="'skeleton-'+i" :id="'skeleton-'+i">
                    <div class="skeleton">

                    </div>
                </div>
            </div>
            <Pagination v-if="isLoading == false && itemFeedResponse?.data?.length != 0" class="pagination flex-row justify-center" :pagination="paginationData" @page-change="setPage" :scrollToTopTarget="scrollTarget"/>

        </div>
    </div>
    </PrimeDrawer>
</template>
<script setup lang="ts">
    import { ref, computed, inject, watch } from "vue";
    import { useItemFeed } from "@/helpers/composables/useItemFeed.ts";
    import { useDateFormat } from "@vueuse/core";
    import Pagination from "@/components/Pagination.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    
    import NewListItem from "@/components/NewListItem.vue";
    import ProductUpdateListItem from "@/components/ProductUpdateListItem.vue";
    import ImageUpdateListItem from "@/components/ImageUpdateListItem.vue";
    import StockUpdateListItem from "@/components/StockUpdatesListItem.vue";
    import DatePicker from "@/components/DatePicker.vue";

    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import CloseIcon from "@icons/closeIcon.svg";
    let showDrawer = defineModel("showDrawer", {
        type: Boolean,
        default: false
    });

    let settings = useSettingsStore();
    let { is_dealer } = storeToRefs(settings);
    watch(is_dealer, (newVal)=>{
        if(newVal == 1){
            fetchItemFeedData();
        }
    })

    let route = inject("route");
    let dateRange = ref(null);
    const tablistRef = ref<HTMLDivElement>();
    const tabId = (label: string) => `${label}-tab`;
    let visibleTab = ref("newly-added");
    const startDate = ref("");
    const endDate = ref("");
    const stockFilterOptions = [
        {
            label: "Back In Stock",
            value: "1",
            name: "stockFilter"
        },
        {
            label: "Out of Stock",
            value: "0",
            name: "stockFilter"
        }
    ];
    const stockFilterValue = ref(stockFilterOptions[0].value);

    const currentFilters = computed(() => {
        let updatedAtFilterValue;
        if (Array.isArray(dateRange.value) && dateRange.value.length > 1) {
            updatedAtFilterValue =
                `${dateRange.value[0] ? useDateFormat(dateRange.value[0], "YYYY-MM-DD").value + " 00:00:00" : ""},` +
                `${dateRange.value[1] ? useDateFormat(dateRange.value[1], "YYYY-MM-DD HH:mm:ss").value + " 23:59:59" : ""}`;
        }
        const obj = {
            ...(updatedAtFilterValue && {
                "filter[updated_at]": updatedAtFilterValue
            }),
            ...(activeTab.value == "stock-updates" &&
            stockFilterValue.value !== null
                ? { "filter[status]": stockFilterValue.value }
                : {})
        };
        return obj
    });

    function closeDrawer() {
        showDrawer.value = false;
    }

    let paginationData = computed(()=>{
        if(itemFeedResponse.value.meta === undefined){
            return {
                totalItems: 0,
                currentPage: 1,
                pageSize: 10,
                maxPages: 10
            }
        }
        return {
            totalItems: itemFeedResponse.value.meta.total,
            currentPage: itemFeedResponse.value.meta.current_page,
            pageSize: itemFeedResponse.value.meta.per_page,
            maxPages: itemFeedResponse.value.meta.last_page
        }
    })

    function setPage (page) {
        if(currentPage.value != page){
            currentPage.value = page;
            loading.value = true;
        }
    }
    let setItemFeedTab = (event) => {
        loading.value = true;
        activeTab.value = event.target.value;
        currentPage.value = 1; // Reset to first page on tab change
        dateRange.value = null; // Reset date range on tab change
        stockFilterValue.value = stockFilterOptions[0].value; // Reset stock filter on tab change

        applyFilters(currentFilters.value);
    };

    const feedPanelHeader = ref();
    const scrollTarget = computed(()=>{
        if(feedPanelHeader.value){
            return feedPanelHeader.value?.parentElement?.parentElement;
        }
        else return null;
    })
    let {
        activeTab,
        activeTabData,
        currentPage,
        fetchItemFeedData,
        applyFilters,
        loading,
        isFetching,
        isLoading,
        itemFeedResponse,
        downloadReport
    } = useItemFeed();
</script>
